import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import {
  Flame,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Coins,
  Gift,
  Rocket,
  Handshake,
  Plus,
  PlayCircle,
  Music,
  Mic,
  UserRoundCheck,
  Tag,
  Smartphone,
  Package,
  Briefcase,
  CalendarDays,
  Lightbulb,
  Gamepad2,
  Shirt,
  BarChart3,
  Trophy,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BoardEntry, DiscoverEntry, EmptyBoard, MockBoardList } from "@/components/board";
import { AllBoardsPodium } from "@/components/all-boards-podium";
import { NativeAdCard } from "@/components/ad-slot";
import { LiveStatsPill } from "@/components/live-stats-pill";
import {
  categoriesQuery,
  categoryBoardQuery,
  categoryHeatQuery,
  discoverListingsQuery,
  generalBoardQuery,
  generalBoardChangesQuery,
  hotActivityQuery,
  boardScopeIds,
} from "@/lib/queries";
import { formatHeat } from "@/lib/behot";
import { SITE_NAME, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

/** Single source of truth for the product grid, JSON-LD and AI summaries. */
const PRODUCTS = [
  {
    key: "hot",
    title: "HOT",
    path: "/hot",
    cta: "Open the charts",
    blurb: "Compete in live paid charts. Available now.",
    seo: "Live paid rankings for creators, artists, startups, apps, brands and products. Boost with Heat to climb any board.",
    soon: false,
  },
  {
    key: "promote",
    title: "PROMOTE",
    path: "/promote",
    cta: "See what's coming",
    blurb: "Directory submissions, playlist pitching, influencer and ads campaigns.",
    seo: "Promotion services: directory and AI-visibility submissions, music playlist pitching to curators, influencer collaborations and simplified Meta/Zeely ad campaigns.",
    soon: true,
  },
  {
    key: "fund",
    title: "FUND",
    path: "/fund",
    cta: "See what's coming",
    blurb: "Crowdfund your project with reward tiers and milestone escrow.",
    seo: "Crowdfunding for creators, artists and startups: project pages, funding goals, reward tiers, backer updates and milestone escrow. Flat 4.85% fee, charged only if the goal is reached.",
    soon: true,
  },
  {
    key: "marketplace",
    title: "MARKETPLACE",
    path: "/marketplace",
    cta: "See what's coming",
    blurb: "Brand and creator collaborations, briefs and paid offers.",
    seo: "Creator marketplace where brands post paid briefs and professional creators publish service offers. Professional profiles activate for 99 Heat; each offer costs 10 Heat.",
    soon: true,
  },
  {
    key: "earn",
    title: "EARN",
    path: "/earn",
    cta: "See what's coming",
    blurb: "Sell attention, content and reach, and withdraw your balance.",
    seo: "Earn from collaborations and content. A 15% platform fee applies; withdrawals start at US$100 by bank transfer (EUR, USD, AED) or USDT/USDC on ERC-20.",
    soon: true,
  },
  {
    key: "refer",
    title: "REFER",
    path: "/referrals",
    cta: "How referrals work",
    blurb: "2 Heat per signup, 10 Heat for every US$100 they spend.",
    seo: "Referral program: earn 2 Heat for every signup and 10 Heat each time someone you referred spends US$100. Reward Heat boosts listings.",
    soon: false,
  },
] as const;

const PRODUCT_ICONS = {
  hot: Flame,
  promote: Sparkles,
  fund: Rocket,
  marketplace: Handshake,
  earn: Coins,
  refer: Gift,
} as const;

export const Route = createFileRoute("/")({
  // Prefetched on the server so the General board, category Heat totals and
  // the discover rail render inside the initial HTML for crawlers.
  loader: async ({ context }) => {
    const categories = await context.queryClient.ensureQueryData(categoriesQuery);
    const first = categories.filter((c) => !c.parent_id)[0];
    const scope = boardScopeIds(categories, first?.id);
    await Promise.all([
      context.queryClient.ensureQueryData(generalBoardQuery),
      context.queryClient.ensureQueryData(categoryHeatQuery),
      context.queryClient.ensureQueryData(hotActivityQuery),
      context.queryClient.ensureQueryData(categoryBoardQuery(scope)),
      context.queryClient.ensureQueryData(discoverListingsQuery(scope)),
    ]);
  },
  head: () =>

    buildPageMetadata({
      path: "/",
      title: "BeHot - Where anything can compete for attention",
      description:
        "Are you an influencer, public figure or celebrity? Or do you have a song, gig, profile, brand, startup, app, product, business, event or even just an idea? Get it on the charts now. List it, boost it, climb, get seen.",
      keywords: [
        "compete for attention",
        "get on the charts",
        "promote your song",
        "promote your brand",
        "promote your app",
        "promote your startup",
        "promote your event",
        "influencer ranking",
        "live charts",
        "get seen online",
        "boost your listing",
        "attention marketplace",
        "creator leaderboard",
        "BeHot",
        "BeHot.Now",
      ],
      ogTitle: "BeHot - Where anything can compete for attention",
      ogDescription:
        "List it → Boost it → Climb → Get seen. More boosts. Higher ranking. More people see you.",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_URL,
          inLanguage: "en",
          description:
            "BeHot is where anything can compete for attention: list your song, gig, profile, brand, startup, app, product, business, event or idea, boost it and climb the live charts.",
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/hot?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: `${SITE_URL}/brand/favicon-512x512.png`,
          description:
            "BeHot is where anything can compete for attention — influencers, songs, gigs, profiles, brands, startups, apps, products, businesses, events and ideas compete on live charts.",
          parentOrganization: {
            "@type": "Organization",
            name: "HRN Innovation Technologies Ltd",
            url: "https://hrn.ae",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "BeHot.Now services",
            itemListElement: PRODUCTS.map((p) => ({
              "@type": "Offer",
              url: `${SITE_URL}${p.path}`,
              availability: p.soon
                ? "https://schema.org/PreOrder"
                : "https://schema.org/InStock",
              itemOffered: {
                "@type": "Service",
                name: `${p.title} — ${SITE_NAME}`,
                url: `${SITE_URL}${p.path}`,
                description: p.seo,
              },
            })),
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "BeHot.Now products",
          itemListElement: PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.title,
            description: p.seo,
            url: `${SITE_URL}${p.path}`,
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to get on the BeHot charts",
          description:
            "List it, boost it, climb, get seen. Four simple steps to put anything on the BeHot charts.",
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "List it",
              text: "Add your song, gig, profile, brand, startup, app, product, business, event or idea with your link.",
              url: `${SITE_URL}/get-hot`,
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Boost it",
              text: "Add Heat to your listing. More boosts mean a higher position on the board.",
              url: `${SITE_URL}/how-it-works`,
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Climb",
              text: "Positions update live. The listing with the most active Heat sits at number one.",
              url: `${SITE_URL}/hot`,
            },
            {
              "@type": "HowToStep",
              position: 4,
              name: "Get seen",
              text: "Visitors browse the charts, click your link and discover you.",
              url: `${SITE_URL}/hot`,
            },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is BeHot?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "BeHot is where anything can compete for attention. You list a song, gig, profile, brand, startup, app, product, business, event or idea, boost it, and climb live public charts so more people see you.",
              },
            },
            {
              "@type": "Question",
              name: "Who is BeHot for?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Influencers, public figures and celebrities, plus anyone with something to show: musicians, event promoters, founders, app makers, shops and brands.",
              },
            },
            {
              "@type": "Question",
              name: "How do I rank higher?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "More boosts means a higher ranking. Your position is the total value of your active boosts on that board, so anyone can climb by boosting or by getting fans to boost them.",
              },
            },
            {
              "@type": "Question",
              name: "How do I start?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Open Get Hot, paste your link, pick a board and place your first boost. Your listing goes live after a quick review.",
              },
            },
          ],
        },
      ],
    }),
  component: Home,

});

function Home() {
  // Keep component-only values inside the component so TanStack's route
  // code-splitting transform includes them in the component bundle.
  const heroCategories = [
    { key: "music", icon: Music, slug: "music" },
    { key: "gigs", icon: Mic, slug: "clubs-venues" },
    { key: "influencers", icon: UserRoundCheck, slug: "influencers" },
    { key: "brands", icon: Tag, slug: "brands" },
    { key: "startups", icon: Rocket, slug: "startups" },
    { key: "apps", icon: Smartphone, slug: "apps" },
    { key: "products", icon: Package, slug: "consumer-tech" },
    { key: "business", icon: Briefcase, slug: "business" },
    { key: "events", icon: CalendarDays, slug: "events" },
    { key: "ideas", icon: Lightbulb, slug: "ai" },
    { key: "gaming", icon: Gamepad2, slug: "gaming" },
    { key: "fashion", icon: Shirt, slug: "fashion" },
  ] as const;

  const heroBenefits = [
    { key: "boosts", icon: BarChart3 },
    { key: "ranking", icon: Trophy },
    { key: "seen", icon: Eye },
  ] as const;

  const { data: categories = [] } = useQuery(categoriesQuery);
  const topLevel = categories.filter((c) => !c.parent_id);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const active = topLevel.find((c) => c.slug === activeSlug) ?? topLevel[0];

  const scope = boardScopeIds(categories, active?.id);
  const { data: general = [], isLoading: generalLoading } = useQuery(generalBoardQuery);
  const { data: boardChanges = [] } = useQuery(generalBoardChangesQuery);
  const movementById = new Map(
    boardChanges.map((c) => [
      c.listing_id,
      {
        isNew: c.is_new,
        previousRank: c.previous_rank,
        justMoved:
          c.moved_at !== null &&
          Date.now() - new Date(c.moved_at).getTime() < 24 * 60 * 60 * 1000,
      },
    ]),
  );

  const { data: heatByCategory } = useQuery(categoryHeatQuery);
  const { data: board = [], isLoading } = useQuery(categoryBoardQuery(scope));
  const { data: discover = [] } = useQuery(discoverListingsQuery(scope));
  const { data: activity = [] } = useQuery(hotActivityQuery);

  const catById = new Map(categories.map((c) => [c.id, c]));
  const boardIds = new Set(board.map((r) => r.listing.id));
  const discoverOnly = discover.filter((l) => !boardIds.has(l.id)).slice(0, 6);

  const categoryTotals = topLevel
    .map((c) => {
      const ids = boardScopeIds(categories, c.id);
      let heat = 0;
      let listings = 0;
      for (const id of ids) {
        const t = heatByCategory?.get(id);
        heat += t?.heat ?? 0;
        listings += t?.listings ?? 0;
      }
      return { category: c, heat, listings };
    })
    .sort((a, b) => b.heat - a.heat);

  const emptyBoards = categoryTotals.filter((row) => row.listings === 0);
  const { t } = useT();


  return (
    <>
      <section className="relative overflow-hidden">
        <div className="bg-glow pointer-events-none absolute inset-0 -top-24 h-[420px]" />
        <div className="relative mx-auto max-w-6xl px-4 pb-8 pt-10 sm:pt-16">
          <h1 className="font-display text-4xl font-bold leading-[1.05] sm:text-6xl">
            {t("home.h1.q")} <span className="text-gradient-hot">BeHot</span>?{"\u00a0"}
            <br />
            <br />
            <br />
            {t("home.h1.a1")}{" "}
            <span className="text-gradient-hot">{t("home.h1.anything")}</span>{" "}
            {t("home.h1.a2")}
          </h1>

          <div className="mt-5 max-w-2xl space-y-1 text-base text-muted-foreground sm:text-lg">
            <p>{t("home.sub1")}</p>
            <p>{t("home.sub2")}</p>
            <Link to="/get-hot" className="block pt-1 font-semibold hover:opacity-90">
              <span className="text-gradient-hot">{t("home.getItOnCharts")}</span>{" "}
              <span className="text-black">{t("home.now")}</span>!
            </Link>
          </div>

          <p className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-sm font-bold sm:text-base">
            <span>{t("home.moreBoosts")}</span>
            <span className="size-1.5 rounded-full bg-primary" />
            <span>{t("home.higherRanking")}</span>
            <span className="size-1.5 rounded-full bg-primary" />
            <span>{t("home.morePeople")}</span>
          </p>

          <div className="card-surface mt-6 p-4 sm:p-5">
            <div className="grid grid-cols-4 gap-x-2 gap-y-4 sm:grid-cols-6 sm:gap-4 lg:grid-cols-12">
              {heroCategories.map(({ key, icon: Icon, slug }) => (
                <Link
                  key={key}
                  to="/c/$slug"
                  params={{ slug }}
                  aria-label={t("home.browseChart", { label: t(`home.cat.${key}`) })}
                  className="flex min-w-0 flex-col items-center gap-1.5 text-center transition-colors hover:text-primary"
                >
                  <Icon className="size-6 shrink-0 text-foreground" strokeWidth={1.6} />
                  <span className="w-full truncate text-[11px] font-medium leading-tight text-muted-foreground sm:text-xs">
                    {t(`home.cat.${key}`)}
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link
                to="/categories"
                aria-label={`${t("home.more")} — ${t("nav.categories")}`}
                className="inline-flex items-center gap-1 text-sm font-bold text-primary"
              >
                <Plus className="size-4" /> {t("home.more")} · {t("nav.categories")}
              </Link>
            </div>
          </div>

          <div className="card-surface mt-4 grid gap-5 p-5 sm:grid-cols-3 sm:divide-x sm:divide-border">
            {heroBenefits.map(({ key, icon: Icon }) => (
              <div key={key} className="flex items-start gap-3 sm:px-4 sm:first:pl-0 sm:last:pr-0">
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-accent">
                  <Icon className="size-5 text-primary" />
                </span>
                <div>
                  <p className="font-display font-bold leading-tight">
                    {t(`home.benefit.${key}.title`)}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t(`home.benefit.${key}.body`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-6 flex flex-wrap items-center justify-center gap-2 font-display text-base font-bold tracking-tight sm:text-lg">
            <span>{t("home.listIt")}</span>
            <ArrowRight className="size-4 text-primary" />
            <span>{t("home.boostIt")}</span>
            <ArrowRight className="size-4 text-primary" />
            <span>{t("home.climb")}</span>
            <ArrowRight className="size-4 text-primary" />
            <span>{t("home.getSeen")}</span>
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild variant="hot" size="lg">
              <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
                {t("nav.getHot")} <Flame className="size-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/how-it-works">
                <PlayCircle className="size-5" /> {t("home.howItWorks")}
              </Link>
            </Button>
          </div>


        </div>
      </section>



      {/* GENERAL BOARD, every category in one ranking */}
      <section className="mx-auto max-w-6xl px-4">
        <div className="mb-4 flex justify-center">
          <LiveStatsPill />
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Flame className="size-5 text-primary" />
              <h2 className="font-display text-xl font-bold tracking-tight">{t("home.generalBoard")}</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("home.generalBoardSub")}
            </p>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/hot">
              {t("home.allBoards")} <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>


        <div className="mt-4 space-y-3">
          {generalLoading && (
            <div className="card-surface p-6 text-sm text-muted-foreground">
              {t("home.loadingBoard")}
            </div>
          )}
          {!generalLoading && general.length === 0 && (
            <>
              <EmptyBoard />
              <MockBoardList seedKey="general" />
            </>
          )}
          {general.slice(0, 10).map((row, i) => {
            const cat = row.top_category_id ? catById.get(row.top_category_id) : undefined;
            return (
              <Fragment key={row.listing.id}>
                <BoardEntry
                  row={row}
                  rank={i + 1}
                  categoryLabel={cat ? { name: cat.name, slug: cat.slug } : undefined}
                  movement={movementById.get(row.listing.id)}
                  showTakePosition
                />
                {(i === 2 || i === 6) && <NativeAdCard slot="boardFeed" />}
              </Fragment>
            );
          })}
        </div>

        <AllBoardsPodium />

        {emptyBoards.length > 0 && (
          <div className="mt-6 rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-background to-accent/20 p-5 sm:p-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary-foreground">
              <Flame className="size-3.5" />{" "}
              {emptyBoards.length === 1
                ? t("home.empty.badgeOne")
                : t("home.empty.badge", { n: emptyBoards.length })}
            </span>
            <h3 className="mt-3 font-display text-xl font-bold sm:text-2xl">
              {t("home.empty.titleA")}{" "}
              <span className="text-gradient-hot">{t("home.empty.titleFirst")}</span>{" "}
              {t("home.empty.titleB")}
            </h3>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {t("home.empty.bodyA")}{" "}
              <strong className="text-foreground">{t("home.empty.bodyPrice")}</strong>{" "}
              {t("home.empty.bodyB")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {emptyBoards.slice(0, 12).map(({ category }) => (
                <Link
                  key={category.id}
                  to="/get-hot"
                  search={{ category: category.slug, listing: undefined }}
                  className="rounded-full border border-primary/40 bg-background/70 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  {category.name} · $5
                </Link>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild variant="hot">
                <Link to="/hot">
                  <Flame className="size-4" /> {t("home.empty.checkBoards")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/app/wallets">{t("home.empty.loadHeat")}</Link>
              </Button>
            </div>
          </div>
        )}
      </section>


      {/* Heat by category, with an All Categories card styled like a boost button */}
      {categoryTotals.length > 0 && (
        <section className="mx-auto mt-12 max-w-6xl px-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Flame className="size-5 text-primary" />
                <h2 className="font-display text-xl font-bold tracking-tight">{t("home.heatByCategory")}</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("home.heatByCategorySub")}
              </p>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/hot">
                {t("home.allBoards")} <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {categoryTotals.map(({ category, heat, listings }) => (
              <Link
                key={category.id}
                to="/c/$slug"
                params={{ slug: category.slug }}
                className="card-surface group flex items-center justify-between gap-3 rounded-xl p-4 transition-transform hover:-translate-y-0.5"
              >
                <div>
                  <p className="font-display text-lg font-bold leading-tight">{category.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatHeat(heat)} Heat · {t("home.listedCount", { n: listings })}
                  </p>
                </div>
                <ArrowRight className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}

            {/* All categories card, styled like a boost button */}
            <Link
              to="/categories"
              className="group flex min-h-24 flex-col items-center justify-center gap-1 rounded-2xl bg-gradient-hot px-5 py-4 text-center text-hot-foreground shadow-hot transition-transform hover:-translate-y-0.5"
            >
              <p className="font-display text-lg font-bold">{t("home.allCategories")}</p>
              <p className="flex items-center gap-1 text-sm font-semibold opacity-90">
                {t("home.viewEveryBoard")}{" "}
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </p>
            </Link>
          </div>
        </section>
      )}

      <section className="defer-paint mx-auto mt-12 max-w-6xl px-4">
        <div className="flex items-center gap-2">
          <Flame className="size-5 text-primary" />
          <h2 className="font-display text-xl font-bold tracking-tight">{t("home.hotNow")}</h2>
        </div>

        <div className="mt-3 -mx-4 flex gap-2 overflow-x-auto px-4 pb-2">
          {topLevel.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveSlug(c.slug)}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                active?.id === c.id
                  ? "border-transparent bg-gradient-hot text-hot-foreground"
                  : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {isLoading && (
            <div className="card-surface p-6 text-sm text-muted-foreground">{t("home.loadingCategoryBoard")}</div>
          )}
          {!isLoading && board.length === 0 && (
            <>
              <EmptyBoard categorySlug={active?.slug} />
              <MockBoardList seedKey={active?.slug ?? "board"} categorySlug={active?.slug} />
            </>
          )}
          {board.map((row, i) => (
            <Fragment key={row.listing.id}>
            <BoardEntry
              row={row}
              rank={i + 1}
              categorySlug={active?.slug}
              categoryLabel={
                row.top_category_id && catById.get(row.top_category_id)
                  ? {
                      name: catById.get(row.top_category_id)!.name,
                      slug: catById.get(row.top_category_id)!.slug,
                    }
                  : undefined
              }
            />
              {(i === 2 || i === 6) && <NativeAdCard slot="boardFeed" />}
            </Fragment>
          ))}
        </div>

        {discoverOnly.length > 0 && (
          <div className="mt-8">
            <h3 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
              {t("home.discoverMore")}
            </h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {discoverOnly.map((l) => (
                <DiscoverEntry key={l.id} listing={l} />
              ))}
            </div>
          </div>
        )}

        {active && (
          <div className="mt-6">
            <Button asChild variant="ghost">
              <Link to="/c/$slug" params={{ slug: active.slug }}>
                {t("home.seeFullBoard", { name: active.name })} <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        )}
      </section>

      {activity.length > 0 && (
        <section className="defer-paint mx-auto mt-12 max-w-6xl px-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="size-5 text-signal" />
            <h2 className="font-display text-xl font-bold tracking-tight">{t("home.latestBoosts")}</h2>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {activity.map((a) => (
              <div key={a.id} className="card-surface flex min-w-0 items-center gap-3 p-3 text-sm">
                <Flame className="size-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate">

                  <strong>{a.listing?.name ?? t("home.aListing")}</strong>{" "}
                  {t("home.activityGained", {
                    heat: formatHeat(a.value_cents),
                    category: a.category?.name ?? "",
                  })}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="defer-paint mx-auto mt-16 max-w-6xl px-4" aria-labelledby="products-heading">
        <h2 id="products-heading" className="font-display text-xl font-bold tracking-tight">
          {t("home.everything.title")}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {t("home.everything.sub")}
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p) => {
            const Icon = PRODUCT_ICONS[p.key];
            return (
              <article key={p.key} className="card-surface flex flex-col p-6">
                <Icon className="size-6 text-primary" />
                <h3 className="mt-3 flex items-center gap-2 font-display text-lg font-bold">
                  {p.title}
                  {p.soon && (
                    <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {t("home.soon")}
                    </span>
                  )}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(`home.product.${p.key}.blurb`)}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {t(`home.product.${p.key}.seo`)}
                </p>
                <Button
                  asChild
                  variant={p.soon ? "outline" : "heat"}
                  size="sm"
                  className="mt-4 self-start"
                >
                  <Link to={p.path}>{t(`home.product.${p.key}.cta`)}</Link>
                </Button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="defer-paint mx-auto mt-16 max-w-6xl px-4 pb-4" aria-labelledby="about-heading">
        <h2 id="about-heading" className="font-display text-xl font-bold tracking-tight">
          {t("home.about.title")}
        </h2>
        <div className="card-surface mt-4 p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
{t("home.about.body")}
          </p>
          <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "fanBoosts",
              "referrals",
              "earnings",
              "fund",
              "marketplace",
              "wallets",
            ].map((f) => (
              <div key={f}>
                <dt className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t(`home.about.${f}.t`)}
                </dt>
                <dd className="mt-1 text-sm text-foreground">{t(`home.about.${f}.d`)}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-5 text-sm text-muted-foreground">
            {t("home.about.newHere")}{" "}
            <Link to="/how-it-works" className="font-semibold text-primary underline">
              {t("home.about.linkGuide")}
            </Link>
            {t("home.about.browse")}{" "}
            <Link to="/categories" className="font-semibold text-primary underline">
              {t("home.about.linkCategories")}
            </Link>{" "}
            {t("home.about.or")}{" "}
            <Link
              to="/get-hot"
              search={{ listing: undefined, category: undefined }}
              className="font-semibold text-primary underline"
            >
              {t("home.about.linkSubmit")}
            </Link>
            .
          </p>
        </div>
      </section>
    </>

  );
}
